import { PDFDocument } from 'pdf-lib';
import { TemplatePage, TemplateRole, PageTemplateMapping, Template } from '../types';
import mammoth from 'mammoth';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export async function pdfToArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('Kon PDF niet lezen'));
      }
    };
    reader.onerror = () => reject(new Error('Fout bij het lezen van het bestand'));
    reader.readAsArrayBuffer(file);
  });
}

export async function getPdfPageCount(pdfData: ArrayBuffer): Promise<number> {
  try {
    const pdfDoc = await PDFDocument.load(pdfData);
    return pdfDoc.getPageCount();
  } catch (error) {
    throw new Error('Kon PDF pagina\'s niet tellen');
  }
}

export async function mergePdfWithTemplate(
  documentData: ArrayBuffer,
  templateData: ArrayBuffer,
  templateOpacity: number = 1.0,
  documentOpacity: number = 1.0,
  ignoreWhiteBackground: boolean = false
): Promise<ArrayBuffer> {
  try {
    const documentPdf = await PDFDocument.load(documentData);
    const templatePdf = await PDFDocument.load(templateData);

    const mergedPdf = await PDFDocument.create();

    // Process each page in the document
    const documentPageCount = documentPdf.getPageCount();

    for (let i = 0; i < documentPageCount; i++) {
      // Copy original document page
      const [docPage] = await mergedPdf.copyPages(documentPdf, [i]);
      const page = mergedPdf.addPage(docPage);

      // Apply template overlay at full opacity
      const [templatePageCopy] = await mergedPdf.copyPages(templatePdf, [0]);
      const embeddedTemplate = await mergedPdf.embedPage(templatePageCopy);

      // Apply template without transparency
      page.drawPage(embeddedTemplate, {
        x: 0,
        y: 0,
        width: page.getWidth(),
        height: page.getHeight()
      });
    }

    return await mergedPdf.save();
  } catch (error) {
    console.error('PDF merge error:', error);
    throw new Error('Kon PDF en template niet samenvoegen');
  }
}

export async function mergePdfWithTemplateBundle(
  documentData: ArrayBuffer,
  template: Template,
  pageMappings: PageTemplateMapping[]
): Promise<ArrayBuffer> {
  try {
    const documentPdf = await PDFDocument.load(documentData);
    const mergedPdf = await PDFDocument.create();
    const documentPageCount = documentPdf.getPageCount();

    // If not a bundle or no pages, fall back to regular merge
    if (!template.isBundle || !template.pages || template.pages.length === 0) {
      return mergePdfWithTemplate(documentData, template.pdfData);
    }

    // Load all template pages
    const templatePdfs = await Promise.all(
      template.pages.map(page => PDFDocument.load(page.pdfData))
    );

    for (let i = 0; i < documentPageCount; i++) {
      const pageNum = i + 1;

      // Copy original document page
      const [docPage] = await mergedPdf.copyPages(documentPdf, [i]);
      const page = mergedPdf.addPage(docPage);

      // Find the mapping for this page
      const mapping = pageMappings.find(m => m.pageNumber === pageNum);
      if (mapping) {
        // Find the template page with this role
        const templatePageIndex = template.pages.findIndex(p => p.role === mapping.role);
        if (templatePageIndex >= 0) {
          const templatePdf = templatePdfs[templatePageIndex];
          const [templatePageCopy] = await mergedPdf.copyPages(templatePdf, [0]);
          const embeddedTemplate = await mergedPdf.embedPage(templatePageCopy);

          // Apply template overlay
          page.drawPage(embeddedTemplate, {
            x: 0,
            y: 0,
            width: page.getWidth(),
            height: page.getHeight()
          });
        }
      }
    }

    return await mergedPdf.save();
  } catch (error) {
    console.error('PDF bundle merge error:', error);
    throw new Error('Kon PDF en template bundle niet samenvoegen');
  }
}

export async function mergePdfWithAlgemeneVoorwaarde(
  documentData: ArrayBuffer,
  templateData: ArrayBuffer | null,
  algemeneVoorwaardeData: ArrayBuffer,
  templateOpacity: number = 1.0,
  documentOpacity: number = 1.0,
  ignoreWhiteBackground: boolean = false,
  template?: Template,
  pageMappings?: PageTemplateMapping[]
): Promise<ArrayBuffer> {
  try {
    const algemeneVoorwaardePdf = await PDFDocument.load(algemeneVoorwaardeData);
    let mergedPdf: PDFDocument;

    // First, merge document with template
    if (template?.isBundle && template.pages && pageMappings) {
      // Use bundle merge
      const mergedData = await mergePdfWithTemplateBundle(
        documentData,
        template,
        pageMappings
      );
      mergedPdf = await PDFDocument.load(mergedData);
    } else if (templateData) {
      // Use regular template merge
      const mergedData = await mergePdfWithTemplate(
        documentData,
        templateData,
        templateOpacity,
        documentOpacity,
        ignoreWhiteBackground
      );
      mergedPdf = await PDFDocument.load(mergedData);
    } else {
      // No template - just load document
      mergedPdf = await PDFDocument.load(documentData);
    }

    // Add all pages from Algemene Voorwaarde at the end
    const finalPdf = await PDFDocument.create();

    // Copy all pages from the merged document
    const mergedPageCount = mergedPdf.getPageCount();
    const mergedPages = await finalPdf.copyPages(
      mergedPdf,
      Array.from({ length: mergedPageCount }, (_, i) => i)
    );
    mergedPages.forEach(page => finalPdf.addPage(page));

    // Add Algemene Voorwaarde pages
    const algemeneVoorwaardePageCount = algemeneVoorwaardePdf.getPageCount();
    const algemeneVoorwaardePages = await finalPdf.copyPages(
      algemeneVoorwaardePdf,
      Array.from({ length: algemeneVoorwaardePageCount }, (_, i) => i)
    );
    algemeneVoorwaardePages.forEach(page => finalPdf.addPage(page));

    return await finalPdf.save();
  } catch (error) {
    console.error('PDF merge with Algemene Voorwaarde error:', error);
    throw new Error('Kon PDF niet samenvoegen met Algemene Voorwaarde');
  }
}

export function downloadPdf(pdfData: ArrayBuffer, filename: string = 'document.pdf') {
  const blob = new Blob([pdfData], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function validatePdfFile(file: File): string | null {
  if (!file.type.includes('pdf')) {
    return 'Alleen PDF bestanden zijn toegestaan';
  }

  if (file.size > 10 * 1024 * 1024) {
    return 'Bestand is te groot (max 10MB)';
  }

  return null;
}

export function validateDocumentFile(file: File): string | null {
  if (!file.type.includes('pdf')) {
    return 'Alleen PDF bestanden zijn toegestaan';
  }

  if (file.size > 10 * 1024 * 1024) {
    return 'Bestand is te groot (max 10MB)';
  }

  return null;
}

export async function convertWordToPdf(file: File): Promise<ArrayBuffer> {
  try {
    // Read the Word file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Convert Word to HTML using mammoth
    const result = await mammoth.convertToHtml({ arrayBuffer });
    const html = result.value;
    console.log('Word to PDF: Converted HTML length:', html.length);
    console.log('Word to PDF: HTML preview:', html.substring(0, 500));

    // Create a full-content render first to measure total height
    const measureContainer = document.createElement('div');
    measureContainer.style.position = 'absolute';
    measureContainer.style.left = '-9999px';
    measureContainer.style.width = '170mm'; // A4 width with margins
    measureContainer.style.padding = '20mm';
    measureContainer.style.backgroundColor = 'white';
    measureContainer.style.fontFamily = 'Arial, sans-serif';
    measureContainer.style.fontSize = '11pt';
    measureContainer.style.lineHeight = '1.6';
    measureContainer.innerHTML = html;
    document.body.appendChild(measureContainer);

    // Wait for rendering
    await new Promise(resolve => setTimeout(resolve, 100));

    const totalHeight = measureContainer.scrollHeight;
    console.log('Word to PDF: Total content height:', totalHeight, 'px');

    // A4 page height at 96 DPI: ~257mm = ~973px (accounting for margins)
    // Estimate number of pages needed
    const pageHeightPx = 973; // ~257mm in pixels at 96 DPI
    const estimatedPages = Math.ceil(totalHeight / pageHeightPx);
    console.log('Word to PDF: Estimated pages needed:', estimatedPages);

    // Render full content to canvas
    const fullCanvas = await html2canvas(measureContainer, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      height: totalHeight,
      windowHeight: totalHeight
    });

    // Remove measurement container
    document.body.removeChild(measureContainer);

    console.log('Word to PDF: Full canvas dimensions:', fullCanvas.width, 'x', fullCanvas.height);

    // Create PDF and split canvas into pages
    const pdf = new jsPDF('p', 'mm', 'a4');
    const contentWidth = 170; // Content width with margins
    const contentHeight = 257; // Content height with margins (297 - 40)

    // Calculate how many pixels fit on one page
    const canvasWidthMm = contentWidth;
    const pixelsPerMm = fullCanvas.width / canvasWidthMm;
    const pageHeightInPixels = contentHeight * pixelsPerMm;

    const totalPages = Math.ceil(fullCanvas.height / pageHeightInPixels);
    console.log('Word to PDF: Will create', totalPages, 'pages');

    for (let pageNum = 0; pageNum < totalPages; pageNum++) {
      if (pageNum > 0) {
        pdf.addPage();
      }

      // Calculate source rectangle for this page
      const sourceY = pageNum * pageHeightInPixels;
      const sourceHeight = Math.min(pageHeightInPixels, fullCanvas.height - sourceY);

      // Create a temporary canvas for this page slice
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = fullCanvas.width;
      pageCanvas.height = sourceHeight;
      const ctx = pageCanvas.getContext('2d');

      if (ctx) {
        // Copy the slice from full canvas
        ctx.drawImage(
          fullCanvas,
          0, sourceY, // Source x, y
          fullCanvas.width, sourceHeight, // Source width, height
          0, 0, // Dest x, y
          fullCanvas.width, sourceHeight // Dest width, height
        );

        // Add to PDF
        const imgData = pageCanvas.toDataURL('image/png');
        const imgHeight = (sourceHeight * contentWidth) / fullCanvas.width;
        pdf.addImage(imgData, 'PNG', 20, 20, contentWidth, imgHeight, undefined, 'FAST');
      }
    }

    const finalPageCount = pdf.getNumberOfPages();
    console.log('Word to PDF: Created', finalPageCount, 'pages in PDF');

    // Convert jsPDF to ArrayBuffer
    const pdfBlob = pdf.output('blob');
    return await pdfBlob.arrayBuffer();
  } catch (error) {
    console.error('Word to PDF conversion error:', error);
    throw new Error('Kon Word bestand niet converteren naar PDF');
  }
}

export async function splitPdfIntoTemplatePages(
  pdfData: ArrayBuffer
): Promise<{ pages: TemplatePage[]; pageCount: number }> {
  try {
    const pdfDoc = await PDFDocument.load(pdfData);
    const pageCount = pdfDoc.getPageCount();

    if (pageCount < 2) {
      throw new Error('Bundle moet minimaal 2 pagina\'s hebben');
    }

    const pages: TemplatePage[] = [];

    // Determine roles based on page count
    // 2 pages: voorblad + vervolgblad
    // 3+ pages: voorblad + vervolgblad + eindblad (rest ignored)
    const roles: TemplateRole[] = [];

    if (pageCount === 2) {
      roles.push('voorblad', 'vervolgblad');
    } else {
      roles.push('voorblad', 'vervolgblad', 'eindblad');
    }

    // Extract each page as a separate PDF
    for (let i = 0; i < Math.min(pageCount, 3); i++) {
      const newPdf = await PDFDocument.create();
      const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
      newPdf.addPage(copiedPage);

      const pageData = await newPdf.save();

      pages.push({
        id: `${Date.now()}-${i}-${Math.random().toString(36).substring(7)}`,
        role: roles[i],
        pdfData: pageData,
      });
    }

    return { pages, pageCount };
  } catch (error) {
    console.error('PDF splitting error:', error);
    throw new Error('Kon PDF niet splitsen in aparte bladen');
  }
}

export function generateDefaultPageMappings(
  documentPageCount: number,
  template: Template
): PageTemplateMapping[] {
  const mappings: PageTemplateMapping[] = [];

  // If it's not a bundle, return empty array (old behavior)
  if (!template.isBundle || !template.pages) {
    return mappings;
  }

  const hasEindblad = template.pages.some(p => p.role === 'eindblad');

  for (let i = 1; i <= documentPageCount; i++) {
    let role: TemplateRole;

    if (i === 1) {
      // First page = voorblad
      role = 'voorblad';
    } else if (i === documentPageCount && hasEindblad) {
      // Last page = eindblad (if bundle has one)
      role = 'eindblad';
    } else {
      // Middle pages = vervolgblad
      role = 'vervolgblad';
    }

    mappings.push({ pageNumber: i, role });
  }

  return mappings;
}